class NoxteeLanding extends HTMLElement {
  connectedCallback() {
    this.bindChoices()
    this.bindGallery()
    this.observeMotion()
    this.bindForm()
    this.bindClaudeTimeline()
    this.bindClaudeToggles()
  }

  bindChoices() {
    const quantity = this.querySelector('[data-nx-quantity]')
    const total = this.querySelector('[data-nx-total]')
    const unitPrice = Number(this.dataset.unitPrice || 0)
    const currency = this.dataset.currency || 'USD'
    this.querySelectorAll('[data-nx-choice]').forEach((button) => {
      button.addEventListener('click', () => {
        const selectedQuantity = Number(button.dataset.quantity || 1)
        this.querySelectorAll('[data-nx-choice]').forEach((choice) => choice.setAttribute('aria-pressed', String(choice === button)))
        if (quantity) quantity.value = selectedQuantity
        if (total && unitPrice) total.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format((unitPrice * selectedQuantity) / 100)
      })
    })
  }

  bindGallery() {
    const main = this.querySelector('[data-nx-gallery-main], .nx-pv-gallery-image')
    if (!main) return
    const thumbs = [...this.querySelectorAll('[data-nx-thumb]')]
    const select = (button) => {
      main.src = button.dataset.src
      main.alt = button.dataset.alt || ''
      thumbs.forEach((thumb) => thumb.setAttribute('aria-current', String(thumb === button)))
    }
    thumbs.forEach((button) => {
      button.addEventListener('click', () => {
        select(button)
      })
    })
    const move = (direction) => {
      if (!thumbs.length) return
      const current = Math.max(0, thumbs.findIndex((thumb) => thumb.getAttribute('aria-current') === 'true'))
      select(thumbs[(current + direction + thumbs.length) % thumbs.length])
    }
    this.querySelector('[data-nx-gallery-prev]')?.addEventListener('click', () => move(-1))
    this.querySelector('[data-nx-gallery-next]')?.addEventListener('click', () => move(1))
  }

  observeMotion() {
    const items = this.querySelectorAll('.nx-reveal, [data-nx-stock]')
    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'))
      return
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.classList.add('is-visible')
        observer.unobserve(entry.target)
      })
    }, { threshold: 0.18 })
    items.forEach((item) => observer.observe(item))
  }

  bindForm() {
    const form = this.querySelector('[data-nx-form]')
    const submit = this.querySelector('[data-nx-checkout]')
    if (!form || !submit) return

    form.addEventListener('submit', (event) => {
      event.preventDefault()
      const selectedTier = this.querySelector('[data-nx-claude-tier].is-selected')
      if (!selectedTier?.dataset.checkoutUrl || submit.disabled) return

      submit.disabled = true
      submit.setAttribute('aria-busy', 'true')

      const checkoutUrl = new URL(selectedTier.dataset.checkoutUrl)
      const sourceParameters = new URLSearchParams(window.location.search)
      sourceParameters.forEach((value, key) => {
        if (key !== 'products') checkoutUrl.searchParams.append(key, value)
      })

      window.location.assign(checkoutUrl.toString())
      window.setTimeout(() => {
        submit.disabled = false
        submit.removeAttribute('aria-busy')
      }, 5000)
    })
  }

  bindClaudeTimeline() {
    const timeline = this.querySelector('[data-nx-timeline]')
    if (!timeline) return
    const steps = [...timeline.querySelectorAll('[data-nx-time-step]')]
    let frame = 0
    const update = () => {
      frame = 0
      const rect = timeline.getBoundingClientRect()
      const viewport = window.innerHeight || 800
      const progress = Math.max(0, Math.min(1, (viewport * 0.72 - rect.top) / rect.height))
      timeline.style.setProperty('--nx-timeline-fill', `${Math.max(0.03, progress) * 100}%`)
      steps.forEach((step, index) => step.classList.toggle('is-active', progress > index / 4 + 0.06))
    }
    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(update)
    }
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    update()
  }

  bindClaudeToggles() {
    const activate = (element, callback) => {
      element.addEventListener('click', callback)
      element.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        callback()
      })
    }

    const totals = ['$49.00', '$98.00', '$147.00']
    const subscriptionTerms = [
      '$49 today for 1 bottle, plus $5.12 shipping. Then $49 every 30 days for 1 bottle. Cancel anytime.',
      '$98 today for 3 bottles. Then $98 every 30 days for 2 bottles. Cancel anytime. Free shipping.',
      '$147 today for 5 bottles. Then $147 every 30 days for 3 bottles. Cancel anytime. Free shipping.'
    ]
    const total = this.querySelector('[data-nx-claude-total]')
    const quantity = this.querySelector('[data-nx-claude-quantity]')
    const products = this.querySelector('[data-nx-form] input[name="products"]')
    const subscriptionCopy = this.querySelector('[data-nx-subscription-copy] strong')
    const tiers = [...this.querySelectorAll('[data-nx-claude-tier]')]
    tiers.forEach((tier) => activate(tier, () => {
      const index = Number(tier.dataset.i || 0)
      tiers.forEach((item) => item.classList.toggle('is-selected', item === tier))
      if (total) total.textContent = totals[index]
      if (quantity) quantity.value = tier.dataset.quantity || '1'
      if (products && tier.dataset.checkoutUrl) {
        const selectedProducts = new URL(tier.dataset.checkoutUrl).searchParams.get('products')
        if (selectedProducts) products.value = selectedProducts
      }
      if (subscriptionCopy) subscriptionCopy.textContent = subscriptionTerms[index]
    }))

    this.querySelectorAll('[data-nx-info-toggle]').forEach((toggle) => activate(toggle, () => {
      const content = this.querySelector(`[data-nx-info-content="${toggle.dataset.i}"]`)
      const symbol = toggle.querySelector('span')
      if (!content) return
      const open = content.style.display !== 'none'
      content.style.display = open ? 'none' : 'block'
      toggle.setAttribute('aria-expanded', String(!open))
      if (symbol) symbol.textContent = open ? '+' : '−'
    }))

    this.querySelectorAll('[data-nx-faq-toggle]').forEach((toggle) => activate(toggle, () => {
      const content = this.querySelector(`[data-nx-faq-content="${toggle.dataset.i}"]`)
      const symbol = toggle.querySelector('span')
      if (!content) return
      const open = content.style.display !== 'none'
      content.style.display = open ? 'none' : 'block'
      if (symbol) symbol.textContent = open ? '+' : '−'
    }))
  }
}

if (!customElements.get('noxtee-landing')) customElements.define('noxtee-landing', NoxteeLanding)
